import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  componentStack: string;
  eventId: string | null;
}

// Evita la pagina bianca: un errore di render in una route viene contenuto qui
// invece di smontare tutta l'app. In dev logghiamo lo stack dei componenti per
// individuare rapidamente la sorgente.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: '', eventId: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    const msg = error?.message || '';
    // Chunk loading failure (file 404 after deploy) or stale SW cache
    // serving a chunk whose exports are undefined -> "reading 'default'".
    // Entrambi si risolvono con un reload che forza il SW a servire chunk freschi.
    const isChunkCorruption =
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Element type is invalid. Received a promise that resolves to') ||
      (error.name === 'TypeError' && msg.includes("reading 'default'") && !sessionStorage.getItem('chunk_reloaded'));
    if (isChunkCorruption) {
      sessionStorage.setItem('chunk_reloaded', '1');
      if ('caches' in window) caches.keys().then(ks => ks.forEach(k => caches.delete(k)));
      if ('serviceWorker' in navigator) navigator.serviceWorker.getRegistrations()
        .then(rs => rs.forEach(r => r.unregister())).catch(() => {});
      window.location.reload();
      return { error: null };
    }
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] Render crash:', error?.message);
    console.error('[ErrorBoundary] Component stack:', info?.componentStack);
    this.setState({ componentStack: info?.componentStack || '' });
    if (import.meta.env.VITE_SENTRY_DSN) {
      import('@sentry/react').then(Sentry => {
        if (!Sentry || typeof Sentry.withScope !== 'function') return;
        Sentry.withScope((scope) => {
          scope.setTag('error.type', 'react_error_boundary');
          scope.setContext('componentStack', { stack: info?.componentStack || '' });
          const eventId = Sentry.captureException(error);
          this.setState({ eventId });
        });
      }).catch(() => {});
    }
  }

  private handleReload = () => {
    if (import.meta.env.VITE_SENTRY_DSN) {
      import('@sentry/react').then(Sentry => {
        if (!Sentry || typeof Sentry.addBreadcrumb !== 'function') return;
        Sentry.addBreadcrumb({ category: 'ui', message: 'ErrorBoundary full reload', level: 'info' });
      }).catch(() => {});
    }
    this.setState({ error: null, componentStack: '', eventId: null });
    window.location.reload();
  };

  private fullReport(): string {
    const e = this.state.error;
    return [
      'ERROR: ' + (e?.message || String(e)),
      '',
      'STACK:',
      e?.stack || '(no stack)',
      '',
      'COMPONENT STACK:',
      this.state.componentStack || '(no component stack)',
    ].join('\n');
  }

  private handleCopy = () => {
    const txt = this.fullReport();
    navigator.clipboard?.writeText(txt).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = txt; document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch { /* no-op */ }
      document.body.removeChild(ta);
    });
  };

  render() {
    if (!this.state.error) return this.props.children;

    // Restiamo in locale: mostriamo SEMPRE lo stacktrace completo a schermo
    // (messaggio + stack + component stack) con un bottone per copiarlo.
    return (
      <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-[var(--bg,#0b0b12)] p-6">
        <div className="max-w-[760px] w-full bg-[var(--surface-1,#15151f)] border border-[var(--border-strong,#2a2a38)] rounded-3xl p-6 shadow-2xl">
          <h2 className="text-[20px] font-extrabold text-text-primary mb-1">Si e' verificato un problema</h2>
          <p className="text-[13px] text-text-muted leading-relaxed mb-4">
            L'interfaccia ha incontrato un errore imprevisto. Copia lo stacktrace e ricarica.
          </p>
          <div className="flex gap-3 mb-4">
            <button
              onClick={this.handleReload}
              className="px-5 py-2.5 bg-purple-600 hover:bg-purple-500 text-white rounded-xl font-bold text-[14px] transition-colors"
            >
              Ricarica
            </button>
            <button
              onClick={this.handleCopy}
              className="px-5 py-2.5 bg-[var(--surface-2,#23232e)] hover:bg-[var(--surface-3,#2e2e3a)] text-text-primary rounded-xl font-bold text-[14px] border border-[var(--border-strong,#2a2a38)] transition-colors"
            >
              Copia stacktrace
            </button>
          </div>
          <pre className="text-left text-[11px] leading-relaxed text-code-text bg-[var(--code-bg)] border border-[var(--code-border)] rounded-xl p-3 overflow-auto max-h-[55vh] whitespace-pre-wrap select-text">
            {this.fullReport()}
          </pre>
          {this.state.eventId && (
            <p className="mt-3 text-[11px] text-text-muted">Sentry ID: {this.state.eventId}</p>
          )}
        </div>
      </div>
    );
  }
}